import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RoleBasedSidebar from './components/RoleBasedSidebar';
import Topbar from './components/Topbar';
import MainContent from './components/MainContent';
import PermissionGate from './components/PermissionGate';

import NoPermission from './components/NoPermission';
import Projects from './pages/projects/Projects';
import Activity from './pages/activity/Activity';
import Library from './pages/library/Library';
import Settings from './pages/settings/Settings';
import Auth from './pages/auth/Auth';
import GoogleCallback from './pages/auth/GoogleCallback';
import PasswordSetup from './pages/auth/PasswordSetup';
import CompanySetup from './pages/auth/CompanySetup';
import NotFound from './pages/notFound/NotFound';
import ForgotPassword from './pages/forgotPassword/ForgotPassword';
import ResetPassword from './pages/resetPassword/ResetPassword';
import BuilderJSEmailBuilder from './pages/emailbuilder/BuilderJSEmailBuilder';
import NewBuilderJSPage from './pages/emailbuilder/NewBuilderJSPage';
import BuilderJS6Page from './pages/emailbuilder/BuilderJS6Page';
import BuilderJS6SimplePage from './pages/emailbuilder/BuilderJS6SimplePage';
import Team from './pages/team/Team';
import TeamMembers from './pages/teamMembers/TeamMembers';
import AcceptInvitation from './pages/acceptInvitation/AcceptInvitation';
import AdminPanel from './pages/adminPanel/AdminPanel';
import RoleManagement from './pages/roleManagement/RoleManagement';
import UserRoleAssignment from './pages/userRoleAssignment/UserRoleAssignment';
import Help from './pages/help/Help';
import Dashboard from './pages/dashboard/Dashboard';
import UserDashboard from './pages/userDashboard/UserDashboard';
import CDN from './pages/cdn/CDN';
import EmailCampaign from './pages/emailCampaign/EmailCampaign';
import EmailBatch from './pages/emailBatch/EmailBatch';
import Cronjobs from './pages/cronjobs/Cronjobs';
import SMTP from './pages/smtp/SMTP';


import { NotificationProvider } from './contexts/NotificationContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { MobileMenuProvider } from './contexts/MobileMenuContext';
import config from './config/frontend.config';
import './App.css';

// Layout wrapper to detect builder pages and add class
const LayoutWrapper = ({ children }) => {
  const location = useLocation();
  const isBuilderPage = location.pathname.includes('/builderjs-email-builder') || 
                       location.pathname.includes('/new-builderjs') ||
                       location.pathname.includes('/builderjs6');
  
  return (
    <div className={`app-layout ${isBuilderPage ? 'builder-layout' : ''}`}>
      {children}
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(config.isAuthenticated());



  // Listen for authentication changes
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = config.isAuthenticated();
      setIsAuthenticated(authStatus);
    };

    // Check auth on mount
    checkAuth();

    // Listen for storage changes (when logout clears localStorage)
    const handleStorageChange = () => {
      checkAuth();
    };

    // Listen for custom login event
    const handleLoginSuccess = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('loginSuccess', handleLoginSuccess);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('loginSuccess', handleLoginSuccess);
    };
  }, [config]);

  return (
    <Router>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={5}
        enableMultiContainer={false}
        preventDuplicates={true}
      />
      <Routes>
        <Route path="/auth" element={isAuthenticated ? <Navigate to="/" replace /> : <Auth />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/auth/company-setup" element={<CompanySetup />} />
        <Route path="/auth/password-setup" element={<PasswordSetup />} />
        <Route path="/forgotpassword" element={<ForgotPassword />} />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/*" element={
          isAuthenticated ? (
            <NotificationProvider>
              <PermissionProvider>
                <MobileMenuProvider>
                  <LayoutWrapper>
                    <RoleBasedSidebar />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Topbar />
                      <MainContent>
                        <Routes>
                    <Route path="/" element={<UserDashboard />} />
                    <Route path="/projects" element={
                      <PermissionGate 
                        requiredPermissions={['canViewProjects']}
                        customMessage="You don't have permission to view projects. Contact your administrator for access."
                      >
                        <Projects />
                      </PermissionGate>
                    } />
                    <Route path="/activity" element={
                      <PermissionGate 
                        requiredPermissions={['canViewActivity']}
                        customMessage="You don't have permission to view activity. Contact your administrator for access."
                      >
                        <Activity />
                      </PermissionGate>
                    } />
                    <Route path="/library" element={
                      <PermissionGate 
                        requiredPermissions={['canViewLibrary']}
                        customMessage="You don't have permission to access the library. Contact your administrator for access."
                      >
                        <Library />
                      </PermissionGate>
                    } />
                    <Route path="/invitations" element={
                      <PermissionGate 
                        requiredPermissions={['canViewInvitations']}
                        customMessage="You don't have permission to view invitations. Contact your administrator for access."
                      >
                        <Team />
                      </PermissionGate>
                    } />
                    <Route path="/team-members" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTeamMembers']}
                        customMessage="You don't have permission to view team members. Contact your administrator for access."
                      >
                        <TeamMembers />
                      </PermissionGate>
                    } />
                    <Route path="/settings" element={
                      <PermissionGate 
                        requiredPermissions={['canViewSettings']}
                        customMessage="You don't have permission to access settings. Contact your administrator for access."
                      >
                        <Settings />
                      </PermissionGate>
                    } />
                    {/* <Route path="/email-builder" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the Email Builder. Contact your administrator for access."
                      >
                        <ModernEmailBuilder />
                      </PermissionGate>
                    } />
                    <Route path="/email-builder/:templateId" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the Email Builder. Contact your administrator for access."
                      >
                        <ModernEmailBuilder />
                      </PermissionGate>
                    } />
                    <Route path="/modern-email-builder" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the Modern Email Builder. Contact your administrator for access."
                      >
                        <ModernEmailBuilder />
                      </PermissionGate>
                    } />
                    <Route path="/modern-email-builder/:templateId" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the Modern Email Builder. Contact your administrator for access."
                      >
                        <ModernEmailBuilder />
                      </PermissionGate>
                    } />
                    <Route path="/advanced-email-builder" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the Advanced Email Builder. Contact your administrator for access."
                      >
                        <AdvancedEmailBuilder />
                      </PermissionGate>
                    } />
                    <Route path="/advanced-email-builder/:templateId" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the Advanced Email Builder. Contact your administrator for access."
                      >
                        <AdvancedEmailBuilder />
                      </PermissionGate>
                    } />
                    <Route path="/ultra-modern-email-builder" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the Ultra Modern Email Builder. Contact your administrator for access."
                      >
                        <UltraModernEmailBuilder />
                      </PermissionGate>
                    } />
                    <Route path="/ultra-modern-email-builder/:templateId" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the Ultra Modern Email Builder. Contact your administrator for access."
                      >
                        <UltraModernEmailBuilder />
                      </PermissionGate>
                    } /> */}
                    <Route path="/builderjs-email-builder" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the BuilderJS Email Builder. Contact your administrator for access."
                      >
                        <BuilderJSEmailBuilder />
                      </PermissionGate>
                    } />
                    <Route path="/builderjs-email-builder/:templateId" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the BuilderJS Email Builder. Contact your administrator for access."
                      >
                        <BuilderJSEmailBuilder />
                      </PermissionGate>
                    } />
                    <Route path="/new-builderjs" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the New BuilderJS. Contact your administrator for access."
                      >
                        <NewBuilderJSPage />
                      </PermissionGate>
                    } />
                    <Route path="/new-builderjs/:id" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access the New BuilderJS. Contact your administrator for access."
                      >
                        <NewBuilderJSPage />
                      </PermissionGate>
                    } />
                    <Route path="/builderjs6" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access BuilderJS 6.0. Contact your administrator for access."
                      >
                        <BuilderJS6Page />
                      </PermissionGate>
                    } />
                    <Route path="/builderjs6/view/:id" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access BuilderJS 6.0. Contact your administrator for access."
                      >
                        <BuilderJS6Page viewMode={true} />
                      </PermissionGate>
                    } />
                    <Route path="/builderjs6/:id" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access BuilderJS 6.0. Contact your administrator for access."
                      >
                        <BuilderJS6Page />
                      </PermissionGate>
                    } />
                    <Route path="/builderjs6-simple" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access BuilderJS 6.0. Contact your administrator for access."
                      >
                        <BuilderJS6SimplePage />
                      </PermissionGate>
                    } />
                    <Route path="/builderjs6-simple/:id" element={
                      <PermissionGate 
                        requiredPermissions={['canViewTemplates']}
                        customMessage="You don't have permission to access BuilderJS 6.0. Contact your administrator for access."
                      >
                        <BuilderJS6SimplePage />
                      </PermissionGate>
                    } />
                    <Route path="/help" element={<Help />} />
                    <Route path="/dashboard" element={<UserDashboard />} />
                    <Route path="/cdn" element={
                      <PermissionGate 
                        requiredPermissions={['canViewImageCDN']}
                        customMessage="You don't have permission to access Image CDN. Contact your administrator for access."
                      >
                        <CDN />
                      </PermissionGate>
                    } />
                    <Route path="/email-campaigns" element={
                      <PermissionGate 
                        requiredPermissions={['canViewEmailCampaigns']}
                        customMessage="You don't have permission to access Email Campaigns. Contact your administrator for access."
                      >
                        <EmailCampaign />
                      </PermissionGate>
                    } />
                    <Route path="/email-batches" element={
                      <PermissionGate 
                        requiredPermissions={['canViewEmailCampaigns']}
                        customMessage="You don't have permission to access Email Batches. Contact your administrator for access."
                      >
                        <EmailBatch />
                      </PermissionGate>
                    } />
                    <Route path="/smtp" element={
                      <PermissionGate 
                        requiredPermissions={['canViewSMTP']}
                        customMessage="You don't have permission to view SMTP configurations. Contact your administrator for access."
                      >
                        <SMTP />
                      </PermissionGate>
                    } />
                    <Route path="/cronjobs" element={
                      <PermissionGate 
                        requiredPermissions={['canViewEmailCampaigns']}
                        customMessage="You don't have permission to access Cronjobs. Contact your administrator for access."
                      >
                        <Cronjobs />
                      </PermissionGate>
                    } />


                    <Route path="/admin-panel" element={
                      <PermissionGate 
                        requiredPermissions={['canAccessAdminPanel']}
                        customMessage="You don't have permission to access the Admin Panel. Only SuperAdmin can access this area."
                      >
                        <AdminPanel />
                      </PermissionGate>
                    } />
                    <Route path="/role-management" element={
                      <PermissionGate 
                        requiredPermissions={['canUpdateRoles']}
                        customMessage="You don't have permission to manage roles. Only SuperAdmin can access role management."
                      >
                        <RoleManagement />
                      </PermissionGate>
                    } />
                    <Route path="/user-role-assignment" element={
                      <PermissionGate 
                        requiredPermissions={['canManageUsers']}
                        customMessage="You don't have permission to assign user roles. Contact your administrator for access."
                      >
                        <UserRoleAssignment />
                      </PermissionGate>
                    } />
                    {/* SuperAdmin specific routes */}
                    <Route path="/system-settings" element={
                      <PermissionGate 
                        requiredPermissions={['canViewSystemSettings']}
                        customMessage="You don't have permission to access System Settings. Only SuperAdmin can access this area."
                      >
                        <NotFound />
                      </PermissionGate>
                    } />
                    <Route path="/all-users" element={
                      <PermissionGate 
                        requiredPermissions={['canManageUsers']}
                        customMessage="You don't have permission to view all users. Only SuperAdmin can access this area."
                      >
                        <NotFound />
                      </PermissionGate>
                    } />
                    <Route path="/all-teams" element={
                      <PermissionGate 
                        requiredPermissions={['canManageAllTeams']}
                        customMessage="You don't have permission to view all teams. Only SuperAdmin can access this area."
                      >
                        <NotFound />
                      </PermissionGate>
                    } />
                    <Route path="/system-activity" element={
                      <PermissionGate 
                        requiredPermissions={['canViewAllActivity']}
                        customMessage="You don't have permission to view system activity. Only SuperAdmin can access this area."
                      >
                        <NotFound />
                      </PermissionGate>
                    } />
                    <Route path="/database" element={
                      <PermissionGate 
                        requiredPermissions={['canAccessAdminPanel']}
                        customMessage="You don't have permission to access the database. Only SuperAdmin can access this area."
                      >
                        <NotFound />
                      </PermissionGate>
                    } />
                    <Route path="/monitoring" element={
                      <PermissionGate 
                        requiredPermissions={['canAccessAdminPanel']}
                        customMessage="You don't have permission to access monitoring. Only SuperAdmin can access this area."
                      >
                        <Dashboard />
                      </PermissionGate>
                    } />
                    <Route path="*" element={<NotFound />} />
                        </Routes>
                      </MainContent>
                    </div>
                  </LayoutWrapper>
                </MobileMenuProvider>
              </PermissionProvider>
            </NotificationProvider>
          ) : (
            <Navigate to="/auth" replace />
          )
        } />
      </Routes>
    </Router>
  );
}

export default App;
